type TokenSource = () => Promise<string | null>

const DEFAULT_API_ERROR_MESSAGE = 'Не удалось загрузить данные. Повторите попытку позже.'

export class ApiResponseError extends Error {
  readonly status: number

  constructor(status: number, message = DEFAULT_API_ERROR_MESSAGE) {
    super(message)
    this.name = 'ApiResponseError'
    this.status = status
  }
}

let tokenSource: TokenSource = async () => null

export function setApiTokenSource(source: TokenSource): () => void {
  tokenSource = source
  return () => {
    if (tokenSource === source) tokenSource = async () => null
  }
}

function isSameOriginApiRequest(input: RequestInfo | URL): boolean {
  const raw = input instanceof Request ? input.url : String(input)
  const url = new URL(raw, window.location.origin)
  return url.origin === window.location.origin && url.pathname.startsWith('/api/')
}

export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  if (!isSameOriginApiRequest(input)) return fetch(input, init)
  const token = await tokenSource()
  const headers = new Headers(input instanceof Request ? input.headers : undefined)
  new Headers(init.headers).forEach((value, key) => headers.set(key, value))
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const method = (init.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()
  if (!token && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith('almaz_csrf='))
    if (csrf) headers.set('X-CSRF-Token', decodeURIComponent(csrf.slice('almaz_csrf='.length)))
  }
  return fetch(input, { ...init, headers, credentials: init.credentials ?? 'same-origin' })
}

function isJsonContentType(value: string | null): boolean {
  if (value === null) return false
  const mediaType = value.split(';', 1)[0].trim().toLowerCase()
  return mediaType === 'application/json' || mediaType.endsWith('+json')
}

function russianErrorMessage(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null
  const detail = 'detail' in payload ? payload.detail : null
  // A refusal body may carry its sentence as `{detail: {code, message}}`.
  const candidate = typeof detail === 'object' && detail !== null && 'message' in detail
    ? detail.message
    : 'detail' in payload
      ? detail
      : 'error' in payload
        ? payload.error
        : null
  if (typeof candidate !== 'string') return null
  const message = candidate.trim()
  return message.length > 0 && message.length <= 240 && /[А-Яа-яЁё]/.test(message)
    ? message
    : null
}

/** Decode a same-origin API response without leaking raw bodies or parser errors. */
export async function readApiJson<T = unknown>(response: Response): Promise<T> {
  const isError = !response.ok
  if (!isJsonContentType(response.headers.get('content-type'))) {
    throw new ApiResponseError(response.status)
  }
  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    throw new ApiResponseError(response.status)
  }
  if (isError) {
    throw new ApiResponseError(
      response.status,
      russianErrorMessage(payload) ?? DEFAULT_API_ERROR_MESSAGE,
    )
  }
  return payload as T
}
